'use client';

import React, { useRef, useState, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Props = {
    children: ReactNode;
    /** 페이지별 커스텀 리프레시(없으면 location.reload()) */
    onRefresh?: () => Promise<void> | void;
    /** 고정 헤더 높이(px) – 스피너를 헤더 아래에서 보이게 */
    headerHeightPx?: number;         // default: 0
    /** PC에서도 마우스/펜 드래그로 테스트 허용 */
    enableDesktopTest?: boolean;     // default: false
};

// 숫자들(필요시 여기만 조정)
const THRESHOLD = 80;       // 트리거 임계치(px)
const DEADZONE = 30;       // 소폭 드래그 무시(px)
const AT_TOP_EPS = 1;       // scrollTop 허용 오차(px)

export default function PullToRefreshTouch({
    children,
    onRefresh,
    headerHeightPx = 0,
    enableDesktopTest = false,
}: Props) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // 상태
    const [isDragging, setIsDragging] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [curY, setCurY] = useState(-100);

    // 제스처 스냅샷
    const startYRef = useRef(0);
    const activePointerIdRef = useRef<number | null>(null);

    // 이 제스처에서 “기준”이 되는 스크롤러(없으면 window)
    const gestureScrollerRef = useRef<HTMLElement | null>(null);

    // 포털(SSR 안전)
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // ───────────── 스크롤러 탐색/판정 유틸 ─────────────
    const isScrollable = (el: HTMLElement) => {
        const s = getComputedStyle(el);
        const oy = s.overflowY;
        const scrollable = oy === 'auto' || oy === 'scroll' || oy === 'overlay';
        return scrollable && el.scrollHeight > el.clientHeight;
    };

    const isVisible = (el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        return r.height > 0 && r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
    };

    const getScrollableCandidates = (root: HTMLElement) => {
        const arr: HTMLElement[] = [];
        root.querySelectorAll<HTMLElement>('[data-ptr-scroller], .overflow-y-auto, .overflow-y-scroll').forEach(el => {
            if (isScrollable(el)) arr.push(el);
        });
        return arr;
    };

    /** 터치 시작 타깃에서 가장 가까운 스크롤 부모 찾기 */
    const findScrollParentFromTarget = (target: EventTarget | null): HTMLElement | null => {
        if (!(target instanceof Element)) return null;
        const root = wrapperRef.current;
        let node: Element | null = target;
        while (node && node !== root) {
            if (node instanceof HTMLElement) {
                if (node.hasAttribute('data-ptr-scroller')) return node;
                if (isScrollable(node)) return node;
            }
            node = node.parentElement;
        }
        // 루트 자체가 스크롤러일 수도
        if (root instanceof HTMLElement && isScrollable(root)) return root;
        return null;
    };

    /** “지금 이 래퍼 안에서, 보이는 스크롤러 중 하나라도 top이 아닌가?” */
    const anyVisibleScrollerNotAtTop = () => {
        const root = wrapperRef.current;
        if (!root) return false;
        const cands = getScrollableCandidates(root);
        for (const el of cands) {
            if (!isVisible(el)) continue;
            if (el.scrollTop > AT_TOP_EPS) return true;
        }
        return false;
    };

    /** 기준 스크롤러 기준으로 top인지, 기준이 없다면: 보이는 스크롤러들도 함께 검사 */
    const atTop = () => {
        const el = gestureScrollerRef.current;
        if (el) return el.scrollTop <= AT_TOP_EPS;
        // 기준이 없더라도: 보이는 자식 스크롤러 중 내려간 게 있으면 top 아님으로 간주
        if (anyVisibleScrollerNotAtTop()) return false;
        // 마지막으로 window 기준
        return (window.pageYOffset || document.documentElement.scrollTop || 0) <= 0;
    };

    // 헤더 + 노치 보정
    const indicatorTop =
        headerHeightPx > 0
            ? `calc(env(safe-area-inset-top) + ${headerHeightPx}px)`
            : 'env(safe-area-inset-top)';

    const reset = useCallback(() => {
        setIsDragging(false);
        setIsRefreshing(false);
        setCurY(-100);
        startYRef.current = 0;
        activePointerIdRef.current = null;
        gestureScrollerRef.current = null;
    }, []);

    const finish = useCallback(() => {
        setTimeout(() => reset(), 500);
    }, [reset]);

    const triggerRefresh = useCallback(() => {
        setIsRefreshing(true);
        setCurY(20); // 손 뗀 직후 잠깐 멈춘 느낌
        try {
            const ret = onRefresh?.();
            if (ret && typeof (ret as Promise<void>).then === 'function') {
                (ret as Promise<void>).then(finish).catch(finish);
            } else if (onRefresh) {
                finish();
            } else {
                location.reload(); // 기본 동작
            }
        } catch {
            finish();
        }
    }, [onRefresh, finish]);

    // ==================== Touch (모바일) ====================
    const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const t = e.touches[0];
        if (!t) return;

        // 이번 제스처의 “기준 스크롤러” 확정
        gestureScrollerRef.current = findScrollParentFromTarget(e.target) || null;

        // 기준이 없더라도, 래퍼 안 보이는 스크롤러 중 하나라도 내려가 있으면 시작 금지
        if (!atTop()) return;

        startYRef.current = t.clientY; // 어디서 눌러도 OK
    }, []);

    const onTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const startY = startYRef.current;
        if (!startY) return;

        // 이동 중에도 top 조건이 깨지면 즉시 취소
        if (!atTop()) { reset(); return; }

        const t = e.touches[0];
        if (!t) return;

        const dy = t.clientY - startY;
        if (dy <= 0) return;
        if (dy < DEADZONE && curY <= 0) return;

        setIsDragging(true);
        setCurY(dy);
    }, [curY, reset]);

    const onTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const t = e.changedTouches[0];
        if (!t) return reset();

        if (!atTop()) return reset();

        const dy = t.clientY - startYRef.current;
        const ok = startYRef.current > 0 && dy > 100 && curY > THRESHOLD;
        if (ok) triggerRefresh();
        else reset();
    }, [curY, triggerRefresh, reset]);

    // ==================== Pointer (PC 테스트) ====================
    const allowPointer = (t: string) =>
        enableDesktopTest && (t === 'mouse' || t === 'pen');

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!allowPointer(e.pointerType)) return;

        gestureScrollerRef.current = findScrollParentFromTarget(e.target) || null;

        if (!atTop()) return;

        activePointerIdRef.current = e.pointerId;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        startYRef.current = e.clientY;
    }, [enableDesktopTest]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!allowPointer(e.pointerType)) return;
        if (activePointerIdRef.current !== e.pointerId) return;

        if (!atTop()) { reset(); return; }

        const dy = e.clientY - startYRef.current;
        if (dy <= 0) return;
        if (dy < DEADZONE && curY <= 0) return;

        setIsDragging(true);
        setCurY(dy);
    }, [curY, enableDesktopTest, reset]);

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!allowPointer(e.pointerType)) return;
        if (activePointerIdRef.current !== e.pointerId) return;
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);

        if (!atTop()) return reset();

        const dy = e.clientY - startYRef.current;
        const ok = startYRef.current > 0 && dy > 100 && curY > THRESHOLD;

        if (ok) triggerRefresh();
        else reset();
    }, [curY, enableDesktopTest, triggerRefresh, reset]);

    const onPointerCancel = useCallback(() => reset(), [reset]);

    // 인디케이터(항상 body 포털로 렌더 → 모달 위에도 보임)
    const show = isDragging || isRefreshing;
    const indicator = show ? (
        <div
            className={[
                'pointer-events-none fixed left-1/2 z-[2147483647]',
                'flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full',
                'bg-black/40 backdrop-blur-sm ring-1 ring-white/15',
                'shadow-[0_4px_24px_rgba(0,0,0,0.35)]',
                'transition-transform will-change-transform',
                'text-white',
            ].join(' ')}
            style={{
                top: headerHeightPx > 0
                    ? `calc(env(safe-area-inset-top) + ${headerHeightPx}px)`
                    : 'env(safe-area-inset-top)',
                transform: `translate(-50%, ${Math.min(curY, 80)}px)`,
            }}
            aria-hidden
        >
            <div
                className={['h-6 w-6', isRefreshing ? '' : 'animate-[spin_0.6s_linear_infinite]'].join(' ')}
                style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.45))' }}
            >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" opacity="0.25" />
                    <path d="M21 12a9 9 0 0 0-9-9" />
                </svg>
            </div>
        </div>
    ) : null;

    return (
        <div
            ref={wrapperRef}
            className="relative [touch-action:pan-y]"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            {...(enableDesktopTest ? {
                onPointerDown,
                onPointerMove,
                onPointerUp,
                onPointerCancel,
            } : {})}
        >
            {mounted && createPortal(indicator, document.body)}
            {children}
        </div>
    );
}
